import React, { useState, useContext, useCallback, useEffect } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import type { UIAnalyticsEventHandler, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Flex } from '@atlaskit/primitives';

import { NetworkStatus } from '@confluence/apollo-relay-interop';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { MoveToPersonalSpaceFlagTrigger } from '@confluence/content-types-utils/entry-points/MoveToPersonalSpaceFlagTrigger.ts';
import { fg } from '@confluence/feature-gating';
import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader.tsx';
import { FIXED_OBJECT_HEADER_SSR_HEIGHT } from '@confluence/content-types-header/entry-points/contentTypesHeaderConstants.ts';
import type { ContentTypeShellProps } from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';
import { ContentTypesHeaderPlaceholder } from '@confluence/content-types-header-base';
import { DATABASE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { DATABASE_VIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { NotFoundContainer } from '@confluence/content-prerequisites/entry-points/NotFoundContainer.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader.tsx';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { ArchivedPageBanner } from '@confluence/archive-pages/entry-points/ArchivedPageBannerV2.ts';
import { useVersionHistoryExperienceTracking } from '@confluence/content-types-utils/entry-points/useVersionHistoryExperienceTracking.ts';
import { useResizeBanner } from '@confluence/content-types-utils/entry-points/useResizeBanner.ts';
import { useIsVersionHistoryEnabled } from '@confluence/content-types-utils/entry-points/useIsVersionHistoryEnabled.ts';
import {
	useIsSidePanelOpen,
	useSidePanelStateActions,
} from '@confluence/side-panel-state-container';
import { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service.ts';
import { useRegisterCommentsPanelCallbacks } from '@confluence/content-types-bridge/entry-points/useRegisterCommentsPanelCallbacks.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
	useBehavior,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/experiment-onboarding-content-type-spotlight/entry-points/OnboardingNthUserContentTypeSpotlight.tsx';
import { ContentTypesObjectSidebarService } from '@confluence/content-types-bridge/entry-points/content-types-object-sidebar-service.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';

import { DatabaseSidebar } from './DatabaseSidebar.tsx';
import { MobileWebView } from './components/DatabaseMobileView.tsx';
import { DATABASE_VIEW_PAGE_CONTENT_SEGMENT, StartDatabasePageLoad } from './perf.config.tsx';
import { databaseHeightContainer } from './database-shell-styles.ts';
import type { DatabaseIdentity } from './database-shell-types.ts';
import { useDatabaseHeaderActions, useOnboardingService } from './hooks/index.ts';
import { useDatabaseParams } from './database-params.ts';
import { DatabaseIframe } from './components/DatabaseIframe.tsx';
import { useDatabaseVersionHistory } from './hooks/useDatabaseVersionHistory.ts';

const databaseShellContainerStyles = xcss({
	height: '100%',
	width: '100%',
	position: 'relative',
	overflow: 'hidden',
});

const contentHeaderWrapperStyles = xcss({
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: 'color.border',
	backgroundColor: 'elevation.surface',
});

const archivedBannerContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
});

// If any additional fixed margins (like the fixed archive banner margin we used to have here) are
// needed for SSR, then add them to SSR_HEADER_HEIGHT because in SSR we can't use ResizeObserver to
// tell us how big the headers actually are.
const SSR_HEADER_HEIGHT = FIXED_OBJECT_HEADER_SSR_HEIGHT;

type DatabaseShellProps = ContentTypeShellProps & {
	spaceId?: string | null;
	spaceName?: string | null;
	identity?: DatabaseIdentity;
	dataSecurityPolicyPageExport: boolean | null;
	dataSecurityPolicyAttachmentDownload: boolean | null;
	flags: FlagsStateContainer;
};

export const DatabaseShellComponent = ({
	spaceKey,
	contentId,
	contentReferenceId,
	loading,
	queryNetworkStatus,
	spaceId,
	spaceName,
	title,
	identity,
	refetch,
	dataSecurityPolicyPageExport,
	dataSecurityPolicyAttachmentDownload,
	flags,
}: DatabaseShellProps) => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const isLoadingNewContent = loading && queryNetworkStatus !== NetworkStatus.refetch;

	const { bannerHeight: archivedBannerHeight, setBannerElement: setArchivedBannerHeight } =
		useResizeBanner(isSSR);

	const {
		bannerHeight: databaseHeaderHeight,
		setBannerElement: setDatabaseHeaderElement,
		setBannerHeight: setDatabaseHeaderHeight,
	} = useResizeBanner(isSSR);

	const { isMobileApp } = useIsMobileApp();
	const shouldShowMobileWebView = shouldShowMobileWeb() && !isMobileApp;
	const contentActions = useDatabaseHeaderActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const onboardingService = useOnboardingService();
	const { userId } = useSessionData();
	const [eventService] = useState(() => new ContentTypeEventService());
	const [objectSidebarService] = useState(() => new ContentTypesObjectSidebarService());

	const params = useDatabaseParams();
	const isSmartLinkEmbed = params['isSmartLinkEmbed'];
	const isRovoParentProduct = params['isRovoParentProduct'];
	const dbVersionHistory = useDatabaseVersionHistory();
	const isVersionHistoryEnabled = useIsVersionHistoryEnabled('database');
	const isSidePanelOpen = useIsSidePanelOpen();
	const { hideSidePanel } = useSidePanelStateActions();

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const behavior = useBehavior();
	const { hideObjectSidebar } = useObjectSidebarActions();

	// Used to clear existing comments data when we leave the space overview page
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	const { clearProcessedData } = useProcessedCommentsDataActions();
	useRegisterCommentsPanelCallbacks();

	useUnreadCommentsSetup({
		pageId: contentId,
	});

	const routeDataRef = useRouteDataRef();

	useEffect(() => {
		return () => {
			if (fg('cc-whiteboards-consolidating-comments')) {
				resetInitialDataLoadedMap();
				clearCommentsData();
			}
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				clearProcessedData();
			}
		};
	}, [clearCommentsData, clearProcessedData, resetInitialDataLoadedMap, contentId]);

	useEffect(() => {
		return () => {
			hideSidePanel();
		};
	}, [hideSidePanel]);

	useEffect(() => {
		// hide sidebar when navigating between different databases
		void hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	useEffect(() => {
		// hide the object sidebar panels when we open version history.
		// when we have the version history panel open, the sidebar control is not visible,
		// so we will never run into the case where we have version history open and then the user
		// opens the comments/details panel
		if (isObjectSidebarShown && isSidePanelOpen) {
			void hideObjectSidebar();
		}
	}, [isObjectSidebarShown, isSidePanelOpen, hideObjectSidebar]);

	const updateDatabaseHeaderEvents: UIAnalyticsEventHandler = useCallback(
		(event: UIAnalyticsEvent) => {
			const newData = {
				objectType: 'database',
			};

			const newAttributes = {
				contentType: 'database',
			};

			event.update((payload) => {
				const mergedAttributes = {
					...payload.data?.attributes,
					...newAttributes,
				};
				return {
					...payload,
					data: {
						...payload.data,
						...newData,
						attributes: mergedAttributes,
					},
				};
			});
		},
		[],
	);
	const isParentProductRovo = isRovoParentProduct && fg('aifc_create_enabled_ga');
	// Always show the Object Header if enabled by the feature gate - but keep the current behavior
	// for the old content header which isn't shown in SSR
	const isHeaderShown = !isSSR || fg('confluence_frontend_content_wrapper');
	const showContentTypesHeader =
		isHeaderShown &&
		!isLoadingNewContent &&
		!params.isSmartLinkEmbed &&
		!isMobileApp &&
		!!userId &&
		!isParentProductRovo;

	// Use a fixed height for the SSR'ed Object Header to avoid layout shift, because ResizeObserver is not available in SSR
	const contentHeaderHeight =
		isSSR && !isMobileApp && fg('confluence_frontend_content_wrapper')
			? SSR_HEADER_HEIGHT
			: databaseHeaderHeight;
	const headerHeight = (!isMobileApp ? archivedBannerHeight : 0) + contentHeaderHeight;

	const showContentTypesHeaderPlaceholder =
		isLoadingNewContent && !params.isSmartLinkEmbed && !isMobileApp && !!userId;

	const { trackExperience } = useVersionHistoryExperienceTracking();

	useEffect(() => {
		const flowQueryParam = routeDataRef.current?.queryParams?.flow;

		if (flowQueryParam === 'restoreVersion') {
			trackExperience({
				contentType: 'database',
				experience: 'restoreExperience',
				task: 'succeed',
			});
		}
	}, [routeDataRef, contentId, trackExperience]);

	if (!loading && !contentReferenceId) {
		experienceTracker.fail({
			name: DATABASE_EXPERIENCE,
			error: new Error('DatabaseContainerQuery did not return valid referenceId'),
		});
		return <NotFoundContainer route={DATABASE_VIEW} spaceKey={spaceKey} />;
	}

	return (
		<>
			<StartDatabasePageLoad contentId={contentId} />
			{expValEquals('confluence_move_to_personal_space_nudge', 'isEnabled', true) && (
				<MoveToPersonalSpaceFlagTrigger
					contentId={contentId}
					contentType="database"
					spaceKey={spaceKey}
				/>
			)}
			<Subscribe to={databaseHeightContainer}>
				{(bannerState: BannerStateContainer) => (
					<Flex direction="column" xcss={databaseShellContainerStyles}>
						{showContentTypesHeaderPlaceholder && <ContentTypesHeaderPlaceholder />}
						{showContentTypesHeader && (
							<AnalyticsListener onEvent={updateDatabaseHeaderEvents}>
								{isVersionHistoryEnabled ? (
									<Box
										testId="pinned-ct-header"
										xcss={contentHeaderWrapperStyles}
										ref={setDatabaseHeaderElement}
									>
										<ContentTypesHeader contentId={contentId} contentActions={contentActions} />
									</Box>
								) : (
									<ContentTypesHeader
										contentId={contentId}
										contentActions={contentActions}
										onHeightChange={setDatabaseHeaderHeight}
									/>
								)}
							</AnalyticsListener>
						)}
						{!isSmartLinkEmbed && !isMobileApp && (
							<Box xcss={archivedBannerContainerStyles} ref={setArchivedBannerHeight}>
								<Box>
									<ArchivedPageBanner contentId={contentId} marginStyle="both" />
								</Box>
							</Box>
						)}
						{shouldShowMobileWebView ? (
							<MobileWebView bannerState={bannerState} />
						) : (
							<>
								<PageSegmentLoadStart metric={DATABASE_VIEW_PAGE_CONTENT_SEGMENT} />
								<DatabaseIframe
									contentId={contentId}
									contentReferenceId={contentReferenceId}
									onboardingService={onboardingService}
									spaceKey={spaceKey}
									dataSecurityPolicyPageExport={dataSecurityPolicyPageExport}
									dataSecurityPolicyAttachmentDownload={dataSecurityPolicyAttachmentDownload}
									identity={identity}
									refetch={refetch}
									spaceId={spaceId}
									spaceName={spaceName}
									title={title}
									loading={loading}
									isSmartLinkEmbed={isSmartLinkEmbed}
									headerHeight={headerHeight}
									bannerState={bannerState}
									isSidePanelOpen={isSidePanelOpen}
									dbVersionHistory={dbVersionHistory}
									objectSidebarService={objectSidebarService}
									flags={flags}
									isRovoParentProduct={isRovoParentProduct}
								/>
								<OnboardingNthUserContentTypeSpotlight
									contentType={ContentType.DATABASE}
									contentId={contentId}
								/>
								<DatabaseSidebar
									openSidePanel={isSidePanelOpen}
									contentId={contentId}
									spaceKey={spaceKey}
									isObjectSidebarShown={isObjectSidebarShown}
									behavior={behavior}
									databaseHeaderHeight={databaseHeaderHeight}
									bannerState={bannerState}
									title={title}
									eventService={eventService}
									dbVersionHistory={dbVersionHistory}
									isRovoParentProduct={isRovoParentProduct}
								/>
								{!isSSR && (
									<>
										<ContentViewedEvent contentId={contentId} />
										{expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) && (
											<CommentsDataBackgroundLoader
												key={`CommentsDataBackgroundLoader-${contentId}`}
												contentId={contentId}
											/>
										)}
									</>
								)}
							</>
						)}
					</Flex>
				)}
			</Subscribe>
		</>
	);
};

export const DatabaseShell = withFlags(DatabaseShellComponent);
